import { sessionStore } from 'utils/storage'
import {
  AIDA_LAYOUT_FLOATING_POSITION_KEY,
  AIDA_LAYOUT_MODE_KEY,
  AIDA_OPEN_KEY,
  LEGACY_AIDA_OVERLAY_SESSION_KEY,
} from '../utils/constants/storage-keys'
import type { AidaFloatingPosition, AidaPresentationMode } from './overlay-store'

export interface AidaOverlaySession {
  open: boolean
  mode: AidaPresentationMode
  floatingPosition: AidaFloatingPosition | null
}

function isPresentationMode(value: unknown): value is AidaPresentationMode {
  return value === 'fullscreen' || value === 'dock' || value === 'floating'
}

function isFloatingPosition(value: unknown): value is AidaFloatingPosition {
  if (!value || typeof value !== 'object' || !('x' in value) || !('y' in value)) {
    return false
  }
  return (
    typeof value.x === 'number' &&
    typeof value.y === 'number' &&
    Number.isFinite(value.x) &&
    Number.isFinite(value.y)
  )
}

function parseOpenFlag(value: string | null): boolean | null {
  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }
  return null
}

function sessionFromParts(
  open: boolean,
  mode: AidaPresentationMode,
  rawPosition: unknown,
): AidaOverlaySession {
  return {
    open,
    mode,
    floatingPosition: isFloatingPosition(rawPosition) ? rawPosition : null,
  }
}

function sessionFromLegacyBlob(stored: unknown): AidaOverlaySession | null {
  if (!stored || typeof stored !== 'object') {
    return null
  }
  const open = 'open' in stored ? stored.open : null
  const mode = 'mode' in stored ? stored.mode : null
  if (typeof open !== 'boolean' || !isPresentationMode(mode)) {
    return null
  }
  const rawPosition = 'floatingPosition' in stored ? stored.floatingPosition : null
  return sessionFromParts(open, mode, rawPosition)
}

export function migrateLegacyOverlaySession() {
  const open = parseOpenFlag(sessionStore.getString(AIDA_OPEN_KEY))
  const mode = sessionStore.getString(AIDA_LAYOUT_MODE_KEY)
  if (open !== null && isPresentationMode(mode)) {
    sessionStore.remove(LEGACY_AIDA_OVERLAY_SESSION_KEY)
    return
  }
  const session = sessionFromLegacyBlob(
    sessionStore.getJSON<unknown>(LEGACY_AIDA_OVERLAY_SESSION_KEY),
  )
  if (session) {
    writeAidaOverlaySession(session)
    return
  }
  sessionStore.remove(LEGACY_AIDA_OVERLAY_SESSION_KEY)
}

export function readAidaOverlaySession(): AidaOverlaySession | null {
  const open = parseOpenFlag(sessionStore.getString(AIDA_OPEN_KEY))
  const mode = sessionStore.getString(AIDA_LAYOUT_MODE_KEY)
  if (open !== null && isPresentationMode(mode)) {
    return sessionFromParts(
      open,
      mode,
      sessionStore.getJSON<unknown>(AIDA_LAYOUT_FLOATING_POSITION_KEY),
    )
  }
  return null
}

export function writeAidaOverlaySession(session: AidaOverlaySession) {
  sessionStore.setString(AIDA_OPEN_KEY, session.open ? 'true' : 'false')
  sessionStore.setString(AIDA_LAYOUT_MODE_KEY, session.mode)
  if (session.floatingPosition) {
    sessionStore.setJSON(AIDA_LAYOUT_FLOATING_POSITION_KEY, session.floatingPosition)
  } else {
    sessionStore.remove(AIDA_LAYOUT_FLOATING_POSITION_KEY)
  }
  sessionStore.remove(LEGACY_AIDA_OVERLAY_SESSION_KEY)
}

export function clearAidaOverlaySession() {
  sessionStore.remove(AIDA_OPEN_KEY)
  sessionStore.remove(AIDA_LAYOUT_MODE_KEY)
  sessionStore.remove(AIDA_LAYOUT_FLOATING_POSITION_KEY)
  sessionStore.remove(LEGACY_AIDA_OVERLAY_SESSION_KEY)
}
